import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Users, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LiveStreamCardProps {
  stream: {
    id: string;
    title: string;
    thumbnail_url: string | null;
    viewer_count: number;
    category: string | null;
    creator: {
      display_name: string;
      avatar_url: string | null;
      username: string;
      is_verified: boolean;
    };
  };
}

export function LiveStreamCard({ stream }: LiveStreamCardProps) {
  const navigate = useNavigate();

  return (
    <Card 
      className="group cursor-pointer overflow-hidden hover:shadow-lg transition-all"
      onClick={() => navigate(`/stream/${stream.id}`)}
    >
      <div className="relative aspect-video bg-muted">
        {stream.thumbnail_url ? (
          <img
            src={stream.thumbnail_url}
            alt={stream.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/20">
            <Users className="w-16 h-16 text-muted-foreground/40" />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge variant="destructive" className="animate-pulse">
            <div className="w-2 h-2 bg-white rounded-full mr-1.5 animate-pulse" />
            LIVE
          </Badge>
        </div>
        {stream.category && (
          <div className="absolute top-2 right-2">
            <Badge variant="secondary" className="bg-black/60 text-white border-none">
              {stream.category}
            </Badge>
          </div>
        )}
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/60 text-white px-2 py-1 rounded text-sm">
          <Eye className="w-3 h-3" />
          {stream.viewer_count}
        </div>
      </div>
      <CardContent className="p-4">
        <h3 className="font-semibold text-lg line-clamp-2 group-hover:text-primary transition-colors mb-2">
          {stream.title}
        </h3>
        <div className="flex items-center gap-2">
          <Avatar className="w-8 h-8">
            <AvatarImage src={stream.creator.avatar_url || undefined} />
            <AvatarFallback>{stream.creator.display_name?.[0] || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate flex items-center gap-1">
              {stream.creator.display_name || stream.creator.username}
              {stream.creator.is_verified && (
                <Badge variant="secondary" className="h-4 px-1 text-xs">✓</Badge>
              )}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
